import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import { getSession } from './endpoints/auth';
import { getCostRates, getMyBalance, getPaymentDetails, listMyBids } from './endpoints/bids';
import { getContactInfo } from './endpoints/contact';
import { listMyTopUps } from './endpoints/funds';
import { getUnreadCount, listNotifications } from './endpoints/notifications';
import { getVehicle, getVehicleFilters, listVehicles } from './endpoints/vehicles';
import { getMyVehicle, listMyInvoices, listMyVehicles } from './endpoints/vehicles-owned';
import { listWatchlist } from './endpoints/watchlist';
import type { VehicleListQuery } from './types';

const VEHICLES_KEY = ['vehicles'] as const;
const SESSION_KEY = ['session'] as const;
const BIDS_KEY = ['bids'] as const;
const BALANCE_KEY = ['balance'] as const;
const RATES_KEY = ['rates'] as const;
const PAYMENT_DETAILS_KEY = ['payment-details'] as const;
const NOTIFICATIONS_KEY = ['notifications'] as const;
const MY_VEHICLES_KEY = ['my-vehicles'] as const;
const INVOICES_KEY = ['invoices'] as const;
const UNREAD_KEY = [...NOTIFICATIONS_KEY, 'unread'] as const;
const TOP_UPS_KEY = ['top-ups'] as const;
const WATCHLIST_KEY = ['watchlist'] as const;
const CONTACT_KEY = ['contact'] as const;

/**
 * Свіжість видачі. Бекенд тримає сторінку провайдера в кеші 10 хвилин, тож
 * частіше перепитувати немає сенсу: прийшла б та сама сторінка, а на
 * промаху кешу — ще й одиниця квоти.
 */
const LIST_STALE_MS = 5 * 60_000;

/**
 * Каталог сторінками за курсором. Загальної кількості провайдер не віддає,
 * тож «Load more» — це fetchNextPage, доки `nextCursor` не стане null.
 * Ключ — сам query: інший фільтр означає інший курсор з першої сторінки.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const catalogueOptions = (query: Omit<VehicleListQuery, 'cursor'>) =>
	infiniteQueryOptions({
		queryKey: [...VEHICLES_KEY, 'catalogue', query] as const,
		queryFn: ({ pageParam }) => listVehicles({ ...query, cursor: pageParam ?? undefined }),
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => lastPage.nextCursor,
		staleTime: LIST_STALE_MS,
		/*
		  Зміна фільтра не блимає скелетоном: попередні лоти лишаються на
		  екрані, поки їдуть нові. На живому провайдері з межею 1 запит/с
		  порожня сітка трималася б секундами після кожного кліку.
		*/
		placeholderData: keepPreviousData,
	});

/**
 * Одна перша сторінка — для головної і схожих лотів. Провайдер віддає лоти
 * за датою торгів, тож перша сторінка — це саме «ті, що закриваються першими».
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const pageOptions = (query: Omit<VehicleListQuery, 'cursor'>) =>
	queryOptions({
		queryKey: [...VEHICLES_KEY, 'page', query] as const,
		queryFn: () => listVehicles(query),
		staleTime: LIST_STALE_MS,
	});

/** Довідник марок і моделей оновлюється рідко: бекенд тримає його добу. */
const filtersOptions = queryOptions({
	queryKey: [...VEHICLES_KEY, 'filters'] as const,
	queryFn: getVehicleFilters,
	staleTime: 60 * 60_000,
});

/**
 * Виняток із правила «явний тип повернення всюди».
 *
 * queryOptions повертає брендований тип, у якому зашитий літеральний queryKey —
 * саме він робить useQuery типобезпечним. Будь-яка рукописна анотація це
 * брендування стирає, тобто зробила б код МЕНШ типобезпечним, а не більш.
 * Гайд у §5.4 прямо вимагає «derive, never duplicate» — тут це той випадок.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const detailOptions = (id: string) =>
	queryOptions({
		queryKey: [...VEHICLES_KEY, 'detail', id] as const,
		queryFn: () => getVehicle(id),
		staleTime: 5 * 60_000,
	});

const sessionOptions = queryOptions({
	queryKey: SESSION_KEY,
	queryFn: getSession,
	/**
	 * Сесія живе в httpOnly-куці, тобто JS її не бачить і не може перевірити
	 * сам. Тому свіжість тримаємо короткою і перепитуємо при поверненні на
	 * вкладку: інакше інтерфейс показував би вхід ще довго після того,
	 * як сесія протухла на сервері.
	 */
	staleTime: 60_000,
	refetchOnWindowFocus: true,
	// 401 — це «не автентифікований», а не збій. Повторювати нема сенсу.
	retry: false,
});

const bidsListOptions = queryOptions({ queryKey: BIDS_KEY, queryFn: listMyBids, staleTime: 30_000 });
const balanceOptions = queryOptions({ queryKey: BALANCE_KEY, queryFn: getMyBalance, staleTime: 30_000 });
const ratesOptions = queryOptions({ queryKey: RATES_KEY, queryFn: getCostRates, staleTime: 60 * 60_000 });
// Реквізити змінюються дуже рідко, але це гроші: годину тримати не можна,
// бо після зміни дилер платив би за старими до перезавантаження вкладки.
const paymentDetailsOptions = queryOptions({
	queryKey: PAYMENT_DETAILS_KEY,
	queryFn: getPaymentDetails,
	staleTime: 5 * 60_000,
});

/**
 * Ставки й баланс. Після подачі ставки інвалідуються ОБИДВА ключі: депозит
 * переходить у Blocked, і баланс у хедері має це показати без перезавантаження.
 */
export const bidQueries = {
	all: (): typeof BIDS_KEY => BIDS_KEY,
	list: (): typeof bidsListOptions => bidsListOptions,
};

export const balanceQueries = {
	all: (): typeof BALANCE_KEY => BALANCE_KEY,
	current: (): typeof balanceOptions => balanceOptions,
};

const notificationsOptions = queryOptions({
	queryKey: NOTIFICATIONS_KEY,
	queryFn: listNotifications,
	staleTime: 30_000,
});
/**
 * Лічильник непрочитаних. Real-time — Phase 2 (WebSocket); на старті
 * опитуємо раз на хвилину, як зафіксовано в stack.md.
 */
const unreadOptions = queryOptions({
	queryKey: UNREAD_KEY,
	queryFn: getUnreadCount,
	staleTime: 30_000,
	refetchInterval: 60_000,
});

const myVehiclesOptions = queryOptions({ queryKey: MY_VEHICLES_KEY, queryFn: listMyVehicles, staleTime: 30_000 });
const invoicesOptions = queryOptions({ queryKey: INVOICES_KEY, queryFn: listMyInvoices, staleTime: 30_000 });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const myVehicleOptions = (id: string) =>
	queryOptions({ queryKey: [...MY_VEHICLES_KEY, id] as const, queryFn: () => getMyVehicle(id), staleTime: 30_000 });

/** Куплені авто й інвойси. Після оплати інвалідуються обидва: статус авто теж змінюється. */
export const myVehicleQueries = {
	all: (): typeof MY_VEHICLES_KEY => MY_VEHICLES_KEY,
	list: (): typeof myVehiclesOptions => myVehiclesOptions,
	detail: (id: string): ReturnType<typeof myVehicleOptions> => myVehicleOptions(id),
};
export const invoiceQueries = {
	all: (): typeof INVOICES_KEY => INVOICES_KEY,
	list: (): typeof invoicesOptions => invoicesOptions,
};

export const notificationQueries = {
	all: (): typeof NOTIFICATIONS_KEY => NOTIFICATIONS_KEY,
	list: (): typeof notificationsOptions => notificationsOptions,
	unread: (): typeof unreadOptions => unreadOptions,
};

/** Канали зв'язку: адреса підтримки з'являється, щойно її задано на бекенді. */
const contactOptions = queryOptions({ queryKey: CONTACT_KEY, queryFn: getContactInfo, staleTime: 60 * 60_000 });

export const contactQueries = {
	info: (): typeof contactOptions => contactOptions,
};

/** Тарифи міняються рідко, тому година свіжості. */
export const ratesQueries = {
	current: (): typeof ratesOptions => ratesOptions,
};

/** Банківські реквізити для оплати інвойса. */
export const paymentDetailsQueries = {
	all: (): typeof PAYMENT_DETAILS_KEY => PAYMENT_DETAILS_KEY,
	current: (): typeof paymentDetailsOptions => paymentDetailsOptions,
};

const topUpsOptions = queryOptions({ queryKey: TOP_UPS_KEY, queryFn: listMyTopUps, staleTime: 30_000 });

/**
 * Поповнення депозиту. Після зарахування оператором змінюються і баланс, і
 * статус запиту — мутації інвалідують обидва ключі.
 */
export const topUpQueries = {
	all: (): typeof TOP_UPS_KEY => TOP_UPS_KEY,
	list: (): typeof topUpsOptions => topUpsOptions,
};

/**
 * Watchlist акаунта. Лише база, без провайдера, тож запит дешевий і стоїть у
 * шапці на кожній сторінці. Гість сюди не ходить — його список у браузері.
 */
const watchlistOptions = queryOptions({ queryKey: WATCHLIST_KEY, queryFn: listWatchlist, staleTime: 60_000 });

export const watchlistQueries = {
	all: (): typeof WATCHLIST_KEY => WATCHLIST_KEY,
	list: (): typeof watchlistOptions => watchlistOptions,
};

/** Сесія поточного користувача. `null` = гість, а не помилка. */
export const sessionQueries = {
	all: (): typeof SESSION_KEY => SESSION_KEY,
	current: (): typeof sessionOptions => sessionOptions,
};

/**
 * Фабрики опцій запиту. Один експорт на запит — щоб loader і хук
 * не заводили два різні ключі кешу і не робили подвійний фетч (§26).
 *
 * Типи повернення виводяться через typeof/ReturnType, а не оголошуються
 * повторно: форма опцій має лишатись однією і тією ж скрізь.
 */
export const vehicleQueries = {
	all: (): typeof VEHICLES_KEY => VEHICLES_KEY,
	catalogue: (query: Omit<VehicleListQuery, 'cursor'>): ReturnType<typeof catalogueOptions> => catalogueOptions(query),
	page: (query: Omit<VehicleListQuery, 'cursor'>): ReturnType<typeof pageOptions> => pageOptions(query),
	filters: (): typeof filtersOptions => filtersOptions,
	detail: (id: string): ReturnType<typeof detailOptions> => detailOptions(id),
};
